/**
 * SayTextSettings.tsx
 * Say Textカードの設定ダイアログの中身（声・速度・高さ）
 */
import { useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import type { NodeSettingsProps } from '../../types';
import {
  DEFAULT_SAY_TEXT_PITCH_SCALE,
  DEFAULT_SAY_TEXT_SPEAKER,
  DEFAULT_SAY_TEXT_SPEED_SCALE,
  SAY_TEXT_PITCH_SCALE_MAX,
  SAY_TEXT_PITCH_SCALE_MIN,
  SAY_TEXT_SPEED_SCALE_MAX,
  SAY_TEXT_SPEED_SCALE_MIN,
  SAY_TEXT_VOICES,
} from '../../config/sayTextVoice';
import { useFlowDisplayLanguage } from '../../displayLanguage';
import { OFFLINE_MODE } from '@/config/offline';
import { OFFLINE_PITCHES, OFFLINE_SPEEDS } from '@/speech/offlineAudioPackage';

export function SayTextSettings({ node }: NodeSettingsProps<'sayText'>) {
  const { updateNodeData } = useReactFlow();
  const displayLanguage = useFlowDisplayLanguage();
  const updateNumber = useCallback((field: 'speaker' | 'speedScale' | 'pitchScale', raw: string) => {
    updateNodeData(node.id, { [field]: Number(raw) });
  }, [node.id, updateNodeData]);

  return (
    <div className="space-y-4 text-sky-700">
      <label className="block" data-product-tour="card-input-1">
        <span className="mb-1 block text-sm font-medium">
          {displayLanguage === 'en' ? 'Voice' : '声の種類'}
        </span>
        <select
          value={node.data.speaker ?? DEFAULT_SAY_TEXT_SPEAKER}
          onChange={(event) => updateNumber('speaker', event.target.value)}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-sky-400"
        >
          {SAY_TEXT_VOICES.map((voice) => (
            <option key={voice.speaker} value={voice.speaker}>{voice.label}</option>
          ))}
        </select>
      </label>
      <label className="block" data-product-tour="card-input-2">
        <span className="mb-1 flex justify-between text-sm font-medium">
          <span>{displayLanguage === 'en' ? 'Speed' : '声のスピード'}</span>
          <span>{(node.data.speedScale ?? DEFAULT_SAY_TEXT_SPEED_SCALE).toFixed(1)}</span>
        </span>
        {OFFLINE_MODE ? <select aria-label="声のスピード" value={node.data.speedScale ?? DEFAULT_SAY_TEXT_SPEED_SCALE}
          onChange={(event) => updateNumber('speedScale', event.target.value)}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base">
          {!OFFLINE_SPEEDS.some((speed) => speed === (node.data.speedScale ?? DEFAULT_SAY_TEXT_SPEED_SCALE)) && <option value={node.data.speedScale}>未対応の速度</option>}
          {OFFLINE_SPEEDS.map((speed) => <option key={speed} value={speed}>{speed.toFixed(1)}</option>)}
        </select> : <input
          type="range"
          min={SAY_TEXT_SPEED_SCALE_MIN}
          max={SAY_TEXT_SPEED_SCALE_MAX}
          step="0.1"
          value={node.data.speedScale ?? DEFAULT_SAY_TEXT_SPEED_SCALE}
          onChange={(event) => updateNumber('speedScale', event.target.value)}
          className="block w-full accent-sky-500"
        />}
      </label>
      <label className="block" data-product-tour="card-input-3">
        <span className="mb-1 flex justify-between text-sm font-medium">
          <span>{displayLanguage === 'en' ? 'Pitch' : '声の高さ'}</span>
          <span>{(node.data.pitchScale ?? DEFAULT_SAY_TEXT_PITCH_SCALE).toFixed(2)}</span>
        </span>
        {OFFLINE_MODE ? <select aria-label="声の高さ" value={node.data.pitchScale ?? DEFAULT_SAY_TEXT_PITCH_SCALE}
          onChange={(event) => updateNumber('pitchScale', event.target.value)}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base">
          {!OFFLINE_PITCHES.some((pitch) => pitch === (node.data.pitchScale ?? DEFAULT_SAY_TEXT_PITCH_SCALE)) && <option value={node.data.pitchScale}>未対応の高さ</option>}
          {OFFLINE_PITCHES.map((pitch) => <option key={pitch} value={pitch}>{pitch.toFixed(2)}</option>)}
        </select> : <input
          type="range"
          min={SAY_TEXT_PITCH_SCALE_MIN}
          max={SAY_TEXT_PITCH_SCALE_MAX}
          step="0.01"
          value={node.data.pitchScale ?? DEFAULT_SAY_TEXT_PITCH_SCALE}
          onChange={(event) => updateNumber('pitchScale', event.target.value)}
          className="block w-full accent-sky-500"
        />}
      </label>
    </div>
  );
}
